import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema } from '../schemas';
import {
	restrictedAreaTriggerSchema,
	restrictedAreaTriggerBodySchema,
} from '../../interfaces/IRestrictedAreaTriggerShared';
import { NotificationTypes } from '../../interfaces/NotificationTypes.enum';

// Dynamic enum schema — auto-includes any new NotificationTypes members
const notificationTypeSchema = z.enum(Object.values(NotificationTypes) as [NotificationTypes, ...NotificationTypes[]]);

const triggerParamsSchema = projectIdParamsSchema.extend({
	triggerId: requiredStringSchema.describe('Customized trigger identifier'),
});

// ============================================================
// Contract
// ============================================================

/**
 * `{ root: true }`: `/projects/:projectId` must precede the collection segment,
 * so the contract-name prefix cannot be prepended automatically. Every path in
 * this file therefore spells `/customizedTriggers` itself — leaving one out
 * silently moves that route to `/api/notification/...` and still compiles.
 */
export const customizedTriggersContract = defineContract(
	'customizedTriggers',
	{
		createCustomizedTrigger: route({
			method: 'POST',
			path: '/projects/:projectId/customizedTriggers',
			params: projectIdParamsSchema,
			body: restrictedAreaTriggerBodySchema,
			response: z.string(),
			operationId: 'createCustomizedTrigger',
			tags: ['Customized Triggers'],
			summary: 'Create a new customized notification trigger',
		}),
		/**
		 * `type` is a FILTER, not a resource id — it selects which triggers of the
		 * project to return. It was `/:triggerType`, a nested-resource costume over
		 * a filtered collection; the playbook prefers the query.
		 */
		getCustomizedTriggers: route({
			method: 'GET',
			path: '/projects/:projectId/customizedTriggers',
			params: projectIdParamsSchema,
			query: z.object({
				type: notificationTypeSchema.describe('Notification type to filter triggers by'),
			}),
			response: z.array(restrictedAreaTriggerSchema),
			operationId: 'getCustomizedTriggersByType',
			tags: ['Customized Triggers'],
			summary: 'Get customized triggers by type for a project',
		}),
		deleteCustomizedTrigger: route({
			method: 'DELETE',
			path: '/projects/:projectId/customizedTriggers/:triggerId',
			params: triggerParamsSchema,
			response: z.string(),
			operationId: 'deleteCustomizedTrigger',
			tags: ['Customized Triggers'],
			summary: 'Delete a customized trigger',
		}),
		updateCustomizedTrigger: route({
			method: 'PUT',
			path: '/projects/:projectId/customizedTriggers/:triggerId',
			params: triggerParamsSchema,
			body: restrictedAreaTriggerBodySchema,
			response: restrictedAreaTriggerSchema,
			operationId: 'updateCustomizedTrigger',
			tags: ['Customized Triggers'],
			summary: 'Update a customized trigger',
		}),
	},
	{ root: true }
);
